import { IndustryLayout } from "@/components/industry/IndustryLayout";
import { IndustryFAQ } from "@/components/industry/IndustryFAQ";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Bot, Target, Users, ArrowRight, BarChart, MessageSquare, Network } from "lucide-react";
import { industryFAQs } from "@/data/industry-faqs";

const Ecommerce = () => {
  return (
    <IndustryLayout industryName="eCommerce">
      {/* Hero Section */}
      <section 
        className="relative py-24 px-4 pt-40"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/90" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold text-white mb-6">
              eCommerce Solutions That Drive Growth
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Transform your online retail business with our comprehensive eCommerce solutions. 
              From platform development to optimization, we help you succeed in the digital marketplace.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">10+</div>
              <p className="text-gray-600">eCommerce Stores Launched</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">25%</div>
              <p className="text-gray-600">Average Revenue Growth</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">15%</div>
              <p className="text-gray-600">Cost Reduction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            AI-Powered eCommerce Solutions
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Bot className="h-8 w-8 text-blue-600" />,
                title: "Intelligent Customer Support",
                description: "24/7 AI agents that handle customer inquiries, process returns, and provide personalized shopping assistance."
              },
              {
                icon: <Target className="h-8 w-8 text-blue-600" />,
                title: "Smart Product Recommendations",
                description: "AI agents that analyze customer behavior and preferences to deliver personalized product suggestions and increase sales."
              },
              {
                icon: <Users className="h-8 w-8 text-blue-600" />,
                title: "Customer Journey Optimization",
                description: "AI-powered analysis of customer interactions to optimize the shopping experience and increase conversion rates."
              },
              {
                icon: <MessageSquare className="h-8 w-8 text-blue-600" />,
                title: "Automated Marketing",
                description: "Intelligent agents that create and manage targeted email campaigns, social media content, and promotional offers."
              },
              {
                icon: <BarChart className="h-8 w-8 text-blue-600" />,
                title: "Inventory Intelligence",
                description: "AI agents that predict demand, optimize stock levels, and automate reordering to prevent stockouts."
              },
              {
                icon: <Network className="h-8 w-8 text-blue-600" />,
                title: "Multi-Channel Integration",
                description: "Smart agents that synchronize inventory, pricing, and orders across all your sales channels automatically."
              }
            ].map((service, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <IndustryFAQ faqs={industryFAQs["ecommerce"]} industryName="eCommerce" />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Transform Your eCommerce Business?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Let's discuss how we can help your online store reach its full potential.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Schedule Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </IndustryLayout>
  );
};

export default Ecommerce;
